import { type CSSProperties, type ReactNode } from 'react';
import {
  cn,
  Menu,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  ChevronRight,
  DotsVertical,
  Download01,
  Expand03,
  Minimize01,
  Printer,
  SearchMd,
  ZoomIn,
  ZoomOut,
} from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';
import { ZOOM_PRESETS, isZoomPreset, toZoomPercent } from './docxZoom';

/**
 * An icons-core icon is a bare `<svg>` with no Chakra in the tree, so a Chakra
 * colour token means nothing to it.
 *
 * Nor does `var(--kula-colors-secondary-700)`: only seven `--kula-colors-*`
 * custom properties exist, and this is not one of them. An undefined var with
 * no fallback makes the whole declaration invalid, so the icon silently
 * inherits instead — which type-checks, lints and renders wrong.
 */
const ICON_COLOR = colors['secondary-700'];

/** Figma 95:14644 — every glyph on this toolbar is 16px. */
const ICON_SIZE = 16;

/** Height of both button groups and the standalone search box (Figma: 34px). */
const CONTROL_SIZE = 34;

/**
 * Figma 95:14644 — the toolbar row.
 *
 * This toolbar is laid out with plain elements and Tailwind rather than Chakra
 * `Flex`/`Box`: nested to this depth, Chakra v2's prop types push the whole
 * subtree past TypeScript's union-complexity limit (TS2590), and the error
 * lands on the root element no matter how the props beneath it are written.
 * Every colour here is the same design token either way.
 */
const BAR_CLASS =
  'flex h-[55px] shrink-0 items-center justify-between gap-1.5 border-b border-secondary-100 bg-white px-3';

interface ToolButtonProps {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Square inside the zoom group, icon-width in the actions group. */
  width?: CSSProperties['width'];
  /**
   * Groups are border-box, so a segment inside one fills the content box rather
   * than restating 34px — otherwise the border pushes the group to 36px.
   */
  height?: CSSProperties['height'];
  /** Divider between segments of the zoom group. */
  hasDivider?: boolean;
  children: ReactNode;
}

const ToolButton = ({
  label,
  onClick,
  disabled,
  width = CONTROL_SIZE,
  height = CONTROL_SIZE,
  hasDivider = false,
  children,
}: ToolButtonProps) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          style={{ width, height }}
          className={cn(
            'inline-flex shrink-0 items-center justify-center transition-colors',
            'enabled:hover:bg-secondary-25',
            'disabled:cursor-not-allowed disabled:opacity-40',
            hasDivider && 'border-r border-secondary-100'
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export interface DocxViewerToolbarProps {
  /**
   * Rendered at the far left of the toolbar, before Search.
   *
   * The e-sign preview puts its token-count pill here so the drawer keeps one
   * toolbar row rather than stacking a second bar above the document.
   */
  leadingSlot?: ReactNode;
  onZoomIn: () => void;
  onZoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  /** Current scale, as a multiplier — `1` is 100%. */
  zoom?: number;
  /** Jump straight to a preset from the percentage picker. */
  onZoomChange?: (zoom: number) => void;
  onDownload: () => void;
  onPrint: () => void;
  isFullscreen?: boolean;
  onFullscreen: () => void;
  onSearchClick?: () => void;
  searchSlot?: ReactNode;
  isPrinting?: boolean;
}

/**
 * Toolbar above the rendered document (Figma 95:14644, "Resume Heading").
 *
 * The controls sit in two bordered groups rather than as loose icon buttons:
 * zoom (out / percentage picker / in) and actions (download / fullscreen /
 * overflow). Print lives in the overflow menu, which is what the design's
 * `dots-vertical` stands for — it is the only action with no room of its own.
 */
export const DocxViewerToolbar = ({
  leadingSlot,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  zoom = 1,
  onZoomChange,
  onDownload,
  onPrint,
  isFullscreen = false,
  onFullscreen,
  onSearchClick,
  searchSlot,
  isPrinting = false,
}: DocxViewerToolbarProps) => {
  const zoomLabel = toZoomPercent(zoom);

  const zoomReadout = (
    <button
      type="button"
      aria-label={`Zoom level, ${zoomLabel}`}
      className="inline-flex h-full shrink-0 items-center justify-center gap-[3px] border-r border-secondary-100 px-1.5 transition-colors hover:bg-secondary-25"
    >
      <Text variant="p">{zoomLabel}</Text>
      <ChevronRight size={ICON_SIZE} color={ICON_COLOR} />
    </button>
  );

  return (
    <div data-test-id="docx-toolbar" className={BAR_CLASS}>
      <div className="flex items-center gap-2">
        {leadingSlot}
        {searchSlot ?? (
          <div
            style={{ width: CONTROL_SIZE, height: CONTROL_SIZE }}
            className="overflow-hidden rounded-md border-[0.75px] border-secondary-200"
          >
            <ToolButton
              label="Search"
              onClick={onSearchClick}
              width="100%"
              height="100%"
            >
              <SearchMd size={ICON_SIZE} color={ICON_COLOR} />
            </ToolButton>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <div
          style={{ height: CONTROL_SIZE }}
          className="flex items-center overflow-hidden rounded-md border border-secondary-100"
        >
          <ToolButton
            label="Zoom out"
            onClick={onZoomOut}
            disabled={!canZoomOut}
            height="100%"
            hasDivider
          >
            <ZoomOut size={ICON_SIZE} color={ICON_COLOR} />
          </ToolButton>
          {onZoomChange ? (
            <Menu
              trigger={zoomReadout}
              triggerAsChild
              side="bottom"
              align="center"
              items={ZOOM_PRESETS.map((preset) => ({
                key: preset,
                label: toZoomPercent(preset),
                checked: isZoomPreset(zoom, preset),
                onClick: () => onZoomChange(preset),
              }))}
            />
          ) : (
            zoomReadout
          )}
          <ToolButton
            label="Zoom in"
            onClick={onZoomIn}
            disabled={!canZoomIn}
            height="100%"
          >
            <ZoomIn size={ICON_SIZE} color={ICON_COLOR} />
          </ToolButton>
        </div>

        <div
          style={{ height: CONTROL_SIZE }}
          className="flex items-center gap-3 rounded-md border-[0.75px] border-secondary-200 bg-white px-3"
        >
          <ToolButton
            label="Download"
            onClick={onDownload}
            width={ICON_SIZE}
            height="100%"
          >
            <Download01 size={ICON_SIZE} color={ICON_COLOR} />
          </ToolButton>
          <ToolButton
            label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={onFullscreen}
            width={ICON_SIZE}
            height="100%"
          >
            {isFullscreen ? (
              <Minimize01 size={ICON_SIZE} color={ICON_COLOR} />
            ) : (
              <Expand03 size={ICON_SIZE} color={ICON_COLOR} />
            )}
          </ToolButton>
          <div className="h-[22px] w-px shrink-0 bg-secondary-100" />
          <Menu
            triggerAsChild
            side="bottom"
            align="end"
            trigger={
              <button
                type="button"
                aria-label="More actions"
                style={{ width: ICON_SIZE }}
                className="inline-flex h-full shrink-0 items-center justify-center"
              >
                <DotsVertical size={ICON_SIZE} color={ICON_COLOR} />
              </button>
            }
            items={[
              {
                key: 'print',
                label: 'Print',
                icon: <Printer size={ICON_SIZE} color={ICON_COLOR} />,
                disabled: isPrinting,
                onClick: onPrint,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};
